<template>
	<SideNavLayout>
		<div class="more-page">
			<router-link to="/settings" class="more-item">
				<AppIcon :name="IconName.Settings" class="more-icon" />
				<span>{{ $t("settings") }}</span>
			</router-link>

			<div v-if="hasAdminAccess" class="more-divider" />

			<router-link
				v-if="hasAdminAccess"
				to="/admin"
				class="more-item"
				@click="posthogClient.navigationClicked('navigation.admin')"
			>
				<AppIcon :name="IconName.Grid" class="more-icon" />
				<span>{{ $t("adminPage.navLabel") }}</span>
			</router-link>

			<a
				:href="buyerProfileUrl"
				target="_blank"
				class="more-item"
				@click="posthogClient.navigationClicked('buyer-profile-action')"
			>
				<AppIcon :name="IconName.UserCircle" class="more-icon" />
				<span>{{ $t("buyer-profile-action") }}</span>
			</a>

			<a
				href="https://www.landerz.ca/privacypolicy"
				target="_blank"
				class="more-item"
				@click="posthogClient.navigationClicked('terms')"
			>
				<AppIcon :name="IconName.FileText" class="more-icon" />
				<span>{{ $t("terms") }}</span>
			</a>

			<div v-if="isUserLoggedIn" class="more-divider" />

			<button v-if="isUserLoggedIn" class="more-item logout-item" @click="handleLogout">
				<AppIcon :name="IconName.LogOut" class="more-icon" :stroke-width="2" />
				<span>{{ $t("logout") }}</span>
			</button>
		</div>
	</SideNavLayout>
</template>

<script setup lang="ts">
import { computed } from "vue"
import { storeToRefs } from "pinia"
import { useRouter } from "vue-router"
import { useI18n } from "vue-i18n"
import AppIcon from "@/components/AppIcon.vue"
import SideNavLayout from "@/components/layout/SideNavLayout.vue"
import { IconName } from "@/components/icons/registry"
import { posthogClient } from "@/utils/posthogClient"
import { Permission, UseRootStore } from "@/model/RootStore"

const router = useRouter()
const store = UseRootStore()
const { isUserLoggedIn } = storeToRefs(store)
const { locale } = useI18n()

const buyerProfileUrl = computed(() =>
	locale.value === "en" ? "https://en.landerz.ca/profil" : "https://www.landerz.ca/profil"
)

const canManageProjects = computed(() => store.currentUser?.has(Permission.ProjectWrite))

const hasAdminAccess = computed(() => {
	return (
		canManageProjects.value ||
		(isUserLoggedIn.value &&
			(store.currentUser?.has(Permission.AuthorizationRead) ||
				store.currentUser?.has(Permission.AuthorizationWrite) ||
				store.currentUser?.has(Permission.DashboardRead)))
	)
})

async function handleLogout(): Promise<void> {
	try {
		await store.logout()
		router.push("/")
	} catch (error) {
		console.error("Error during logout:", error)
		router.push("/login")
	}
}
</script>

<style scoped>
.more-page {
	padding: var(--spacing-sm) 0;
	font-family: var(--font-family-primary);

	.more-item {
		display: flex;
		align-items: center;
		gap: var(--spacing-sm);
		width: 100%;
		padding: var(--spacing-md) var(--spacing-lg);
		border: none;
		background: none;
		text-align: left;
		text-decoration: none;
		color: var(--neutral-600);
		font-size: var(--font-size-base);
		cursor: pointer;

		.more-icon {
			flex-shrink: 0;
			color: currentColor;
		}

		&:active {
			background: var(--neutral-100);
		}
	}

	.logout-item {
		color: var(--accent-warning);
	}

	.more-divider {
		height: var(--border-width-thin);
		background: var(--neutral-200);
		margin: var(--spacing-xs) var(--spacing-lg);
	}
}
</style>
